import {
  canUserAuditOtherOrgs,
  isUserAdmin,
  isUserOrgAdmin,
  isUserSkydioInOrg,
  isUserSkydioReadonly,
  isUserSkydioSuperuser,
} from "state/current_user/selectors";

import { useSelector } from "./redux";
import { useFeatureFlag } from "./useFeatureFlag";

/**
 * Hook to determine if the user should see admin UI menus.
 * True if auditing other orgs, ADMIN org permission, or user is in-org support
 *
 * @returns true if the user is an admin or a support user
 */
export function useIsAdminVisible() {
  const supportUserEnabled = useFeatureFlag("supportUserExpiry");
  const isAdmin = useSelector(isUserAdmin);
  const isSkydioInOrg = useSelector(isUserSkydioInOrg);
  return useSelector(() => isAdmin || (supportUserEnabled && isSkydioInOrg));
}

/**
 * Hook to determine if the user has read-only access to org settings.
 * True if in-org support FF enabled & user is RO user
 *
 * @returns true if user has Read-only access to org settings
 */
export function useIsAdminReadonly() {
  const supportUserEnabled = useFeatureFlag("supportUserExpiry");
  const isSkydioReadonly = useSelector(isUserSkydioReadonly);
  return useSelector(() => supportUserEnabled && isSkydioReadonly);
}

/**
 *
 * Hook to determine if the user is an admin.
 * True if cross-org access enabled, user is in-org ADMIN, or in-org support FF enabled & user is in org SUPERUSER
 *
 *
 * @returns true if the user is auditing, in org admin, or SUPERUSER if FF enabled
 */
export function useIsAdmin() {
  const supportUserEnabled = useFeatureFlag("supportUserExpiry");
  const isAdmin = useSelector(isUserAdmin);
  const isSkydioSuperuser = useSelector(isUserSkydioSuperuser);
  return useSelector(() => isAdmin || (supportUserEnabled && isSkydioSuperuser));
}

/**
 * Hook to determine if developer settings should be shown.
 * True if cross-org access enabled or user is in-org support
 *
 * @returns true if auditing or user is in-org support
 */
export function useShowInternalSettings() {
  const isSkydioInternal = useSelector(canUserAuditOtherOrgs);
  const supportUserEnabled = useFeatureFlag("supportUserExpiry");
  const isSkydioInOrg = useSelector(isUserSkydioInOrg);
  return useSelector(() => isSkydioInternal || (supportUserEnabled && isSkydioInOrg));
}

/**
 * Hook to determine if the user can edit developer settings.
 * True if cross-org access enabled or user is in-org SUPERUSER
 *
 * @returns true if auditing or user is in-org SUPERUSER
 */
export function useEditInternalSettings() {
  const isSkydioInternal = useSelector(canUserAuditOtherOrgs);
  const supportUserEnabled = useFeatureFlag("supportUserExpiry");
  const isSkydioSuperuser = useSelector(isUserSkydioSuperuser);
  return useSelector(() => isSkydioInternal || (supportUserEnabled && isSkydioSuperuser));
}

/**
 *
 * @returns true if the user is a Skydio superuser
 */
export function useIsSkydioSuperuser() {
  const isSkydioSuperuser = useSelector(isUserSkydioSuperuser);
  const supportUserEnabled = useFeatureFlag("supportUserExpiry");
  return useSelector(() => isSkydioSuperuser && supportUserEnabled);
}

/**
 * Hook to determine if the user is an org admin.
 * True user is in-org ADMIN or user is in org SUPERUSER
 *
 * @returns true if the user is in org admin or SUPERUSER if FF enabled
 */
export function useIsOrgAdmin() {
  const isOrgAdmin = useSelector(isUserOrgAdmin);
  const isSuperuser = useIsSkydioSuperuser();
  return useSelector(() => isOrgAdmin || isSuperuser);
}

/**
 *
 * @returns true if the user is an in-org support user
 */
export function useIsUserSkydioInOrg() {
  const supportUserEnabled = useFeatureFlag("supportUserExpiry");
  const isSkydioInOrg = useSelector(isUserSkydioInOrg);
  return useSelector(() => supportUserEnabled && isSkydioInOrg);
}
